import { Component, HostListener, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '../store';
import { Course } from '../models/course.model';
import { combineLatest, Observable, throwError } from 'rxjs';
import { selectActiveCourse, selectActiveCourseDescription, selectUserCoursesIds, selectUserPermissions, } from '../store/selectors';
import { Lesson } from '../models/lesson.model';
import { CourseSection } from '../models/course-section.model';
import { catchError, concatMap, filter, first, map, tap } from 'rxjs/operators';

import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { coursePurchased } from '../store/course.actions';
import { PurchasesService } from '../services/purchases.service';
import { Meta, Title } from '@angular/platform-browser';
import { CourseCoupon, isValidCoupon } from '../models/coupon.model';

import { UrlBuilderService } from '../services/url-builder.service';

import { MessagesService } from '../shared/services/messages.service';

import { EMPTY_IMG } from '../common/ui-constants';
import { TrackingService } from '../services/tracking.service';
import { CourseCouponsDbService } from '../services/course-coupons-db.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { goToLoginPage } from '../common/platform-utils';
import { TenantService } from '../services/tenant.service';
import { PaypalDialogData, showPaypalDialog, } from '../paypal-dialog/paypal-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { scrollIntoViewWithOffset } from '../common/scroll-utils';
import { isAnonymousUser } from '../models/user.model';
import { LoadingService } from '../shared/services/loading.service';
import { PaymentsService } from '../services/payments.service';
import dayjs from "dayjs";
import {Timestamp} from '@firebase/firestore';
import { coursePageDataSelector } from './course-page-selectors';
import { CoursePageData } from './course-page-data.model';
import { CouponsService } from '../services/coupons.service';
import { DurationPipe } from '../shared/pipes/duration.pipe';
import { SafeHtmlPipe } from '../shared/pipes/safe-html.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { PageFooterComponent } from '../shared/page-footer/page-footer.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { ViewHtmlComponent } from '../shared/view-html/view-html.component';
import { CourseContentComponent } from '../course-content/course-content.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { AsyncPipe, CurrencyPipe, DatePipe, NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import { UserActivityLogService } from "../services/user-activity-log.service";
import { isPlanAllowed } from '../shared/allow-plan/is-plan-allowed';

declare const Stripe;

@Component({
  selector: "course",
  templateUrl: "./course-page.component.html",
  styleUrls: ["./course-page.component.scss"],
  standalone: true,
  imports: [
    NgIf,
    NgStyle,
    NgFor,
    RouterLink,
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    CourseContentComponent,
    ViewHtmlComponent,
    MatExpansionModule,
    NgClass,
    PageFooterComponent,
    AsyncPipe,
    CurrencyPipe,
    DatePipe,
    TranslateModule,
    SafeHtmlPipe,
    DurationPipe,
  ],
})
export class CoursePageComponent implements OnInit {
  data$: Observable<CoursePageData>;

  courseDescription$: Observable<string>;

  redeemCouponBoxVisible = false;

  couponCode: string;

  awardUrl: string;

  iconImgUrl: string;

  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private messages: MessagesService,
    private purchases: PurchasesService,
    private title: Title,
    private meta: Meta,
    private ub: UrlBuilderService,
    private router: Router,
    private tracking: TrackingService,
    private dbCoupons: CourseCouponsDbService,
    private snackBar: MatSnackBar,
    private tenant: TenantService,
    private dialog: MatDialog,
    private loading: LoadingService,
    private payments: PaymentsService,
    private coupons: CouponsService,
    private userActivityLogService: UserActivityLogService
  ) { }

  isFixed = false;

  @HostListener("window:scroll", ["$event"])
  onWindowScroll() {
    const MIN_WIDTH = 991;
    const coursePage = document.querySelector(".coursesPage") as HTMLElement;

    const windowWidth = window.innerWidth;
    const isMinWidthReached = windowWidth >= MIN_WIDTH;

    const coursePageHeight = coursePage.offsetHeight;
    const scrollThreshold = coursePageHeight > 1 ? 0.6 : 0;

    this.isFixed = window.pageYOffset >= scrollThreshold;

    if (isMinWidthReached) {
      coursePage.classList.toggle("course-scroll", this.isFixed);
      coursePage.classList.remove("course-scroll-sm");
    } else {
      coursePage.classList.toggle("course-scroll-sm", this.isFixed);
      coursePage.classList.remove("course-scroll");
    }

  }



  ngOnInit() {
    this.awardUrl = this.ub.buildImageUrl("/images/course-page/icons/award-v2.svg");

    this.iconImgUrl = this.ub.buildImageUrl("/images/course-page/icons/edit.svg");

    this.couponCode = this.route.snapshot.queryParamMap.get("couponCode");

    this.route.queryParamMap.subscribe((params) => {
      const purchaseResult = params.get("purchaseResult");

      if (purchaseResult == "success") {
        const ongoingPurchaseSessionId = params.get("ongoingPurchaseSessionId"),
          courseId = params.get("courseId");

        window.history.replaceState(null, null, window.location.pathname);

        if (purchaseResult == "success") {
          this.processPurchaseCompletion(ongoingPurchaseSessionId);
        } else if (purchaseResult == "failure") {
          // this.messages.error('Payment failed, please check your card balance.');
          this.messages.i18nError(
            "course.messages.paymentFailedCheckCardBalance"
          );
        }
      } else if (purchaseResult == "failure") {
        this.messages.i18nError(
          "course.messages.paymentFailedCheckCardBalance"
        );
      }
    });

    const course$ = this.store.pipe(select(selectActiveCourse));

    // do the side effects separately, to ensure they are not triggered multiple times dues to multiple view subscriptions
    course$
      .pipe(
        tap((course) => {
          this.title.setTitle(course.title);

          this.meta.updateTag({
            name: "description",
            content: course.shortDescription,
          });

          this.meta.updateTag({ name: "twitter:card", content: "summary" });
          this.meta.updateTag({
            name: "twitter:site",
            content: "@och_platform",
          });
          this.meta.updateTag({ name: "twitter:title", content: course.title });
          this.meta.updateTag({
            name: "twitter:description",
            content: course.subTitle,
          });
          this.meta.updateTag({
            name: "twitter:text:description",
            content: course.shortDescription,
          });
          this.meta.updateTag({
            name: "twitter:image",
            content: this.thumbnailUrl(course),
          });

          this.meta.updateTag({
            name: "og:image",
            content: this.thumbnailUrl(course),
          });
        })
      )
      .subscribe();

    const userPermissions$ = this.store.pipe(select(selectUserPermissions));

    this.data$ = this.createCoursePageDataObservable(this.couponCode)
        .pipe(
            tap(data =>console.log(`course page data:` , data))
        );

    this.courseDescription$ = this.store.pipe(
      select(selectActiveCourseDescription),
      filter((description) => !!description)
    );

    const userCourses$ = this.store.pipe(select(selectUserCoursesIds));

    // unpublished courses are only visible to admins, or to users that have already bough them
    combineLatest([course$, userPermissions$, userCourses$])
      .pipe(
        filter(([course, permissions]) => !!course && !!permissions),
        first(),
        tap(([course, permissions, userCourses]) => {
          if (
            course.status !== "published" &&
            !permissions.isAdmin &&
            !userCourses.includes(course.id)
          ) {
            this.messages.i18nError(
              "course.messages.courseIsCurrentlyNotPublish"
            );

            setTimeout(() => {
              this.router.navigateByUrl("/courses");
              this.messages.clear();
            }, 3000);
          }
        })
      )
      .subscribe();

    this.tracking.reportStudentViewContent();
  }

  createCoursePageDataObservable(
    couponCode: string
  ): Observable<CoursePageData> {
    const selectData$ = this.store.pipe(select(coursePageDataSelector));

    if (!couponCode) {
      return selectData$;
    } else {
      const course$ = this.store.pipe(select(selectActiveCourse));

      const loadCoupon$ = course$.pipe(
        concatMap((course) =>
          this.dbCoupons.findCouponByCode(course.id, couponCode).pipe(
            tap((coupon) => {
              if (!coupon) {
                this.messages.i18nError(
                  "course.messages.couldNotFindCouponCode",
                  {
                    couponCode,
                  }
                );
              } else if (!isValidCoupon(coupon)) {
                this.messages.i18nWarn(
                  "course.messages.couponCodeIsNoLongerValid",
                  {
                    couponCode: coupon.code,
                  }
                );
              } else {
                if (coupon.free) {
                  this.messages.i18nSuccess(
                    "course.messages.freeCouponIsValidClaimYourFreeCourse"
                  );
                }
              }
            })
          )
        )
      );

      return combineLatest([selectData$, loadCoupon$]).pipe(
        map(([data, coupon]) => {
          return {
            ...data,
            coupon,
            couponValid: isValidCoupon(coupon),
          };
        })
      );
    }
  }

  processPurchaseCompletion(ongoingPurchaseSessionId: string) {
    this.purchases
      .waitForPurchaseCompletion(
        ongoingPurchaseSessionId,
        "course.messages.purchaseSuccessful"
      )
      .subscribe((purchaseSession) => {
        this.tracking.reportStudentPurchase(purchaseSession.price);

        this.store.dispatch(
          coursePurchased({ courseId: purchaseSession.courseId })
        );
      });
  }

  calculateDiscountPercentage(course: Course, coupon: CourseCoupon) {
    return Math.round((100 * (course.price - coupon.price)) / course.price);
  }

  thumbnailUrl(course: Course) {
    return this.ub.buildCourseThumbnailUrl(course);
  }

  onPlaySampleLesson(
    course: Course,
    sections: CourseSection[],
    lessons: Lesson[]
  ) {
    // go to the first lesson, or to the first lesson of the course if no free lessons exist
    if (lessons && lessons.length > 0) {
      let previewLesson = lessons.find((lesson) => lesson.free);
      if (!previewLesson) {
        previewLesson = lessons[0];
      }

      const section = sections.find(
        (section) => section.id == previewLesson.sectionId
      );

      this.router.navigate([
        "/courses",
        course.url,
        section.seqNo,
        "lessons",
        previewLesson.seqNo,
      ]);
    }
  }

  emptyImg() {
    return EMPTY_IMG;
  }

  isCourseVisible(data: CoursePageData) {
    return !this.isAccessDeniedToHiddenCourse(data);
  }

  isAccessDeniedToHiddenCourse(data: CoursePageData) {
    const hiddenCourse = data?.course?.hiddenCourseOn,
      isAdmin = data?.permissions?.isAdmin,
      ownsCourse = data?.canUserAccessCourse;

    // always allow access to admins, or if the course is not hidden
    let allowed = isAdmin || !hiddenCourse;

    if (hiddenCourse) {
      // allow access to the hidden course if the user owns the course
      if (ownsCourse) {
        allowed = true;
      }
      // if there is a coupon, wait for it to be loaded before taking the final decision
      else if (this.couponCode && !data?.coupon) {
        allowed = true;
      }
      // if the course is hidden and the user does not own it, allow access to it if there is a free coupon active
      // the free coupon will allow the user to activate the hidden course
      else if (this.couponCode && data?.coupon) {
        allowed = data.coupon.free;
      }
    }

    return !allowed;
  }

  activateCoupon(data: CoursePageData, newCouponCode: string) {
    if (!newCouponCode) {
      this.messages.i18nError("course.messages.enterCouponCodeFirst");
      return;
    }

    this.data$ = this.createCoursePageDataObservable(newCouponCode);
  }

  onRedeemCoupon() {
    this.redeemCouponBoxVisible = true;
  }

  isCourseEditable(data: CoursePageData) {
    return (
      data?.isAdmin ||
      (data?.isCourseAdmin && data?.allowedCourses.includes(data?.course?.id))
    );
  }

  getDaysLeft(deadline: Timestamp): number {
    if (!deadline) {
      return 0;
    }

    const now = dayjs();
    const expiresAt = dayjs(deadline.toDate());

    return expiresAt.diff(now, "day");
  }

  onStartPaypalPurchase(data: CoursePageData) {
    if (isAnonymousUser(data?.user)) {
      this.redirectToLoginPage(data);
      return;
    }

    const dialogData: PaypalDialogData = {
      ...data,
    };

    showPaypalDialog(this.dialog, dialogData).subscribe();
  }

  scrollTo(navigationTab: HTMLElement, tabNumber: number) {
    const ids = [
      "scroll-overview",
      "scroll-content",
      "scroll-description",
      "scroll-about",
    ];

    const target = document.getElementById(ids[tabNumber]);

    const scrollDown =
      target.getBoundingClientRect().top -
      navigationTab.getBoundingClientRect().top;

    console.log(scrollDown);

    const offset = scrollDown > 0 ? 460 : 180;

    scrollIntoViewWithOffset(ids[tabNumber], offset);
  }

  onBuyCourseWithStripe(data: CoursePageData) {
    if (isAnonymousUser(data?.user)) {
      this.redirectToLoginPage(data);
      return;
    }

    this.initiateCheckoutWithStripe(data);
  }

  redirectToLoginPage(data: CoursePageData) {
    let redirectUrl = `courses/${data.course.url}?`;

    if (data.couponValid) {
      redirectUrl += `couponCode=${data?.coupon.code}`;
    }

    goToLoginPage(this.router, false, this.tenant.id, redirectUrl);
  }

  initiateCheckoutWithStripe(data: CoursePageData) {
    const course = data.course;

    this.messages.i18nInfo(
      "course.courseActionButtons.messages.preparingCheckoutPleaseWait"
    );

    const courseUrl = `${window.location.protocol}//${window.location.host}/courses/${course.url}`;

    const purchaseCourseSession$ = this.payments
      .createPurchaseCourseSession(
        course.id,
        courseUrl,
        data?.coupon?.code ?? null
      )
      .pipe(
        catchError((err) => {
          console.log("Error purchasing course: ", err?.error);

          if (err?.error.error?.raw?.message) {
            this.messages.error(err?.error.error?.raw?.message);
          }
          else {
            this.messages.i18nError("course.courseActionButtons.messages.errorPurchasingCourse");
          }

          return throwError(err);
        })
      );

    const waitForFacebookEvent$ =
      this.tracking.waitForEvent("InitiateCheckout");

    const request$ = waitForFacebookEvent$.pipe(
      concatMap(() => purchaseCourseSession$)
    );

    this.loading.showLoaderUntilCompleted(request$).subscribe((session) => {
      const stripe = Stripe(session.stripePublicKey, {
        stripeAccount: session.stripeTenantUserId,
      });
      stripe.redirectToCheckout({
        sessionId: session.sessionId,
      });
    });
  }

  async startCourse(data: CoursePageData) {
    if (isAnonymousUser(data.user)) {
      goToLoginPage(this.router, false, this.tenant.id);
      return;
    }

    const firstLesson = data.lessons?.find(
      (lesson) => lesson.status == "published"
    );

    const section = data.sections?.find(
      (section) => section.id == firstLesson.sectionId
    );

    await this.router.navigateByUrl(
      `courses/${data.course.url}/${section?.seqNo}/lessons/${firstLesson?.seqNo}`
    );
  }

  onBuyCourseScrolledMobile(data: CoursePageData) {
    if (data?.tenant?.paypalConfig?.enabled) {
      this.onStartPaypalPurchase(data);
    } else if (data?.tenant?.isConnectedToStripe) {
      this.onBuyCourseWithStripe(data);
    }
  }

  isSubscribeButtonVisible(data: CoursePageData) {
    return (
      data.tenant.isConnectedToStripe &&
      !!data.tenant?.pricingPlans &&
      !data.tenant?.subscriptionScreenDisabled
    );
  }

  onClaimFreeCourseCoupon(data: CoursePageData) {
    if (isAnonymousUser(data?.user)) {
      this.redirectToLoginPage(data);
      return;
    }

    this.messages.i18nInfo(
      "course.courseActionButtons.messages.activateFreeCourseThePageWillReload"
    );

    this.coupons
      .fulfillFreeCourseCoupon(data.course?.id, data?.coupon.code)
      .subscribe(
        (res: any) => {

          console.log("res: ", res);

          if (res.exists) {
            console.log("Course already used: ", data?.course.id);
            this.messages.clear();
            this.messages.i18nInfo(
              "course.courseActionButtons.messages.errorActivatingFreeCourse")
          }
          else if (res.disabled) {
            console.log("Coupon disabled: ", res.couponCode);
            this.messages.i18nError("course.courseActionButtons.messages.disabledFreeCoupon", {couponCode: res.couponCode})
          }

          this.store.dispatch(coursePurchased({ courseId: data?.course.id }));

          setTimeout(() => {
            window.location.href = "/courses";
          }, 4000);
        },
        (err) => {
          this.messages.clear();

          this.messages.i18nError(
            "course.courseActionButtons.messages.theFreeCourseCouldNotBeActivated"
          );
        }
      );
  }

  isCertificatesActive(data: CoursePageData) {
    return isPlanAllowed(data?.tenant, "pro") && !data?.course?.certificate?.disabled;
  }
}
